import * as React from "react";
import { useEffect, useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useStyles } from "../../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PopUp from "../../popup/PopUp";
import { externalpostApi, externalgetApi } from "../../../services/externalApis";
import { useSelector } from "react-redux";
import { GRN_API_11, SETTING_API_17 } from "../../../api/ApiModel";

import { BUTTON_LABELS, ITEM_LABELS } from "../../../utils/sys/GrnLabels";
import { GRN_STATUS } from "../../../utils/sys/GrnConstants";
import {
  CON_GRN_STATUS_RETURN,
  CON_GRN_STATUS_REVOKE,
  CON_STATUS_ACTIVE,
} from "../../../utils/sys/Constants";
import { AnyForNow } from "../../../features/grn/types";


//  Map actionType → reasoncatcode
const REASON_CATCODE_MAP = {
  Approve: "1",
  Reject: "2",
  Return: "3",
};

interface ApprovalActionDialogProps {
  open: boolean;
  handleClose: () => void;
  actionType: string;
  grnCodes?: string[];
  enUser?: AnyForNow;
  onSuccess?: () => void;
  resattachments?: AnyForNow[];
}

const ApprovalActionDialog: React.FC<ApprovalActionDialogProps> = ({
  open,
  handleClose,
  actionType,
  grnCodes = [],
  enUser,
  onSuccess,
  resattachments = [],
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [isChecked, setIsChecked] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [remark, setRemark] = useState("");
  const [reason, setReason] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const SYS_USER = useSelector((state: any) => state.user) || {};
   const { LOCCODE, STAFFMAPLOCODE, RESTCODE, USERNAME, COMPCODE } =
    SYS_USER.USER_PARAM || {};


  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setIsChecked(false);
      setDocumentType("");
      setRemark("");
    }
  }, [open]);

  //  Fetch reason options based on actionType
  useEffect(() => {
    const fetchReasons = async () => {
      if (!actionType) return;

      const reasoncatcode = REASON_CATCODE_MAP[actionType];
      if (!reasoncatcode) return;

      setLoading(true);
      try {
        const result = await externalgetApi(SETTING_API_17, "get", {
          COMPCODE,
             LOCCODE: STAFFMAPLOCODE,
          reasoncatcode,
          STS: CON_STATUS_ACTIVE,
        });
        console.log(`API result for ${reasoncatcode}:`, result);
        setReasonOptions(result || []);
      } catch (error) {
        console.error("Error fetching reason options:", error);
        setReasonOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReasons();
  }, [actionType, COMPCODE, LOCCODE]);

 const handleSubmit = async () => {
  const statusCode =
    actionType === BUTTON_LABELS.APPROVE
      ? GRN_STATUS.APPROVE
      : actionType === BUTTON_LABELS.REJECT
      ? CON_GRN_STATUS_REVOKE
      : CON_GRN_STATUS_RETURN;

const payload = {
  grnBulkStsDet: grnCodes.map((grncode) => ({
    grncode: grncode || "",
    descrip: remark,
    reason: reason,
    enuser: USERNAME,
    grnreturncode:"", // Add this if you have return codes to send
    documents: "",
    sts: statusCode,
    trsts: statusCode,
    dtoReasonDOCs:
      (resattachments && resattachments.length > 0
        ? resattachments.map((att) => ({
            doctypecode: att.doctypecode || "",
            doctypeothername: att.doctypeothername || "",
            docpathjson: att.docpathjson || "",
            filename: att.docpathjson || "",
            bytarr: "",
            remark: att.remark || att.docremark || "",
          }))
        : [
            {
              doctypecode: "",
              doctypeothername: "",
              docpathjson: "",
              filename: "",
              bytarr: "",
              remark: "",
            },
          ]),
  })),
  compcode: COMPCODE || "",
  businesstypecode: "O", // Replace with your actual business type code if needed
};

  console.log("Bulk Payload:", payload);

  try {
    const response = await externalpostApi(GRN_API_11, payload);
    console.log("Bulk Response:", response);

    handleClose();
    onSuccess?.();
  } catch (error) {
    console.error("Error submitting approval:", error);
  }
};

  const getTitle = () => {
    switch (actionType) {
      case BUTTON_LABELS.APPROVE:
        return "GRN Approval";
      case BUTTON_LABELS.REJECT:
        return "GRN Rejection";
      case "Return":
        return "GRN Return";
      default:
        return "Action";
    }
  };

  const getReasonLabel = () => {
    switch (actionType) {
      case BUTTON_LABELS.APPROVE:
        return "Reason for Approval";
      case BUTTON_LABELS.REJECT:
        return "Reason for Rejection";
      case "Return":
        return "Reason for Return";
      default:
        return ITEM_LABELS.REASON;
    }
  };

  const content = (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
        <FormControl variant="outlined" fullWidth size="small">
  <InputLabel>{getReasonLabel()}</InputLabel>
  <Select
    value={reason}
    onChange={(e) => setReason(e.target.value)}
    label={getReasonLabel()}
    disabled={loading}
  >
    {reasonOptions.map((option, index) => (
      <MenuItem
        key={index}
        value={option.descrip || option}
      >
        {option.descrip || option}
      </MenuItem>
    ))}
  </Select>
</FormControl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id={ITEM_LABELS.REMARK}
            label={ITEM_LABELS.REMARK}
            variant="outlined"
            size="small"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
             inputProps={{
                    maxLength: 300,
                  }}
                 helperText={
    remark.length >= 300
      ? "Maximum 300 characters allowed"
      : ""
  }
                  error={remark.length > 300}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: remark.length >= 300 ? "red" : "inherit" },
                  }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const actions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        px: 2,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            color="primary"
          />
        }
        label="I confirm that the above action is correct."
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          sx={styles.btn_secondary}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={styles.btn_primary}
          onClick={handleSubmit}
          disabled={!isChecked}
        >
          Ok
        </Button>
      </Box>
    </Box>
  );

  return (
    <PopUp
      open={open}
      handleClose={handleClose}
      title={getTitle()}
      content={content}
      actions={actions}
      maxWidth="sm"
    />
  );
};

export default ApprovalActionDialog;
